import { useState } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormProvider } from 'BrokerTsLib/src/rhf'

import type { PolicyVersionItem } from '@/dtos/exceptionalDiscount'
import { formatDate } from '@/utils/formatDate'
import { AmountInput } from '../components/AmountInput'
import { DiffChip } from '../components/DiffChip'
import { diffOf, fmtKc } from '../lib/premiumMath'
import { isDiscountableRisk } from '../lib/risks'
import { tomorrowIso, useSlevaForm } from '../useSlevaForm'

const gridTemplate = 'minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)'

const HeaderCell = ({ children, align }: { children: string, align?: 'left' | 'right' }) => (
  <Text
    fontSize="2xs"
    fontWeight="bold"
    letterSpacing="0.04em"
    textTransform="uppercase"
    color="gray.500"
    textAlign={align ?? 'left'}
    px={4}
    py={2.5}
    borderBottomWidth="1px"
    borderColor="white"
  >
    {children}
  </Text>
)

const PriceCell = ({ value, base, muted }: { value: number, base?: number, muted?: boolean }) => (
  <VStack alignItems="flex-end" spacing={1} px={4} py={2} borderBottomWidth="1px" borderColor="white" justifyContent="center">
    <Text fontWeight="bold" fontSize="md" color={muted ? 'gray.500' : 'gray.800'}>{fmtKc(value)}</Text>
    {!muted && base !== undefined && <DiffChip delta={diffOf(base, value)} />}
  </VStack>
)

export const SlevaDialog = ({ isOpen, onClose, activeVersion, insuranceId, onSubmit, isSubmitting, submitError }: {
  isOpen: boolean
  onClose: () => void
  activeVersion: PolicyVersionItem
  insuranceId: number
  onSubmit: (request: ReturnType<ReturnType<typeof useSlevaForm>['toRequest']>) => void
  isSubmitting: boolean
  submitError?: string
}) => {
  const discountable = activeVersion.risks.filter(risk => isDiscountableRisk(risk.riskType))
  const addons = activeVersion.risks.filter(risk => !isDiscountableRisk(risk.riskType))
  const [addonsOpen, setAddonsOpen] = useState(false)

  const { methods, previewOf, setRiskValue, setRiskMode, canSubmit, toRequest } = useSlevaForm(discountable)
  const { register, watch, handleSubmit } = methods
  const risks = watch('risks')

  const sumBase = activeVersion.risks.reduce((acc, risk) => acc + (risk.premiumFrequencyClient ?? 0), 0)
  const sumNew = activeVersion.risks.reduce(
    (acc, risk) => acc + (isDiscountableRisk(risk.riskType) ? previewOf(risk.riskType).newPeriod : (risk.premiumFrequencyClient ?? 0)),
    0,
  )

  const submit = handleSubmit(values => onSubmit(toRequest(insuranceId, values)))

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Mimořádná sleva − klient
          <Text fontSize="sm" fontWeight="normal" color="gray.500" mt={1}>
            Zadejte slevu pro jednotlivá rizika — procentem, nebo částkou.
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <FormProvider {...methods}>
          <form onSubmit={submit} noValidate>
            <ModalBody>
              <VStack alignItems="stretch" spacing={4}>
                <Flex gap={5} alignItems="flex-end" flexWrap="wrap">
                  <Box flex="0 0 200px">
                    <Text fontSize="sm" color="gray.500" mb={1.5}>Počátek</Text>
                    <Input type="date" min={tomorrowIso()} fontWeight="bold" {...register('effectiveDate')} />
                  </Box>
                  <Box flex="0 0 200px">
                    <Text fontSize="sm" color="gray.500" mb={1.5}>Konec (výročí)</Text>
                    <Input
                      value={formatDate(new Date(activeVersion.validTo))}
                      isReadOnly
                      tabIndex={-1}
                      bg="gray.50"
                      fontWeight="bold"
                      color="gray.600"
                    />
                  </Box>
                </Flex>
                <Text fontSize="xs" color="gray.500" mt={-2}>
                  Počátek je nejdříve následující den, konec vždy do nejbližšího výročí.
                </Text>

                <Grid templateColumns={gridTemplate} bg="gray.50" borderRadius="sm">
                  <HeaderCell>Riziko</HeaderCell>
                  <HeaderCell>Sleva</HeaderCell>
                  <HeaderCell align="right">Splátka</HeaderCell>
                  <HeaderCell align="right">Celkem</HeaderCell>

                  {discountable.map(risk => {
                    const entry = risks?.[String(risk.riskType)]
                    const preview = previewOf(risk.riskType)
                    return (
                      <Box key={risk.riskType} display="contents">
                        <Flex flexDirection="column" justifyContent="center" px={4} py={2} borderBottomWidth="1px" borderColor="white" gap={1}>
                          <Text fontSize="sm" color="gray.500">{risk.name ?? ''}</Text>
                          <Text fontSize="xs" color="gray.400">{fmtKc(risk.premiumFrequencyClient ?? 0)} / měs</Text>
                        </Flex>
                        <Flex flexDirection="column" justifyContent="center" px={4} py={2} borderBottomWidth="1px" borderColor="white">
                          <AmountInput
                            value={entry?.value ?? '0'}
                            mode={entry?.mode ?? 'kc'}
                            onValueChange={value => setRiskValue(risk.riskType, value)}
                            onModeChange={mode => setRiskMode(risk.riskType, mode)}
                          />
                        </Flex>
                        <PriceCell value={preview.newPeriod} base={risk.premiumFrequencyClient ?? 0} />
                        <PriceCell value={preview.newAnnual} base={(risk.premiumFrequencyClient ?? 0) * 12} />
                      </Box>
                    )
                  })}

                  {addons.length > 0 && (
                    <>
                      <GridItem colSpan={4} bg="white">
                        <Flex alignItems="center" justifyContent="center" position="relative" py={2.5} cursor="pointer" onClick={() => setAddonsOpen(open => !open)}>
                          <Box position="absolute" left={4} right={4} top="50%" h="1px" bg="gray.100" />
                          <HStack spacing={1.5} px={3} bg="white" position="relative" color="gray.500">
                            <Text fontSize="xs">
                              {addonsOpen ? 'Sbalit doplňková připojištění' : `Zobrazit doplňková připojištění (${addons.length})`}
                            </Text>
                            <Icon
                              as={FontAwesomeIcon}
                              icon={faChevronDown}
                              boxSize={3}
                              transform={addonsOpen ? 'rotate(180deg)' : undefined}
                              transition="transform .15s ease"
                            />
                          </HStack>
                        </Flex>
                      </GridItem>
                      {addonsOpen && addons.map(risk => (
                        <Box key={risk.riskType} display="contents">
                          <Flex flexDirection="column" justifyContent="center" px={4} py={2} borderBottomWidth="1px" borderColor="white">
                            <Text fontSize="sm" color="gray.500">{risk.name ?? ''}</Text>
                          </Flex>
                          <Box borderBottomWidth="1px" borderColor="white" />
                          <PriceCell value={risk.premiumFrequencyClient ?? 0} muted />
                          <PriceCell value={(risk.premiumFrequencyClient ?? 0) * 12} muted />
                        </Box>
                      ))}
                    </>
                  )}

                  <GridItem colSpan={2} px={4} py={3} borderTopWidth="2px" borderColor="white">
                    <Text fontWeight="bold" fontSize="sm">Celkem za všechna rizika</Text>
                  </GridItem>
                  <VStack alignItems="flex-end" spacing={1} px={4} py={3} borderTopWidth="2px" borderColor="white">
                    {sumBase !== sumNew && (
                      <Text fontSize="md" color="gray.500" textDecoration="line-through">{fmtKc(sumBase)}</Text>
                    )}
                    <Text fontWeight="bold" fontSize="lg">{fmtKc(sumNew)}</Text>
                    <DiffChip delta={diffOf(sumBase, sumNew)} />
                  </VStack>
                  <VStack alignItems="flex-end" spacing={1} px={4} py={3} borderTopWidth="2px" borderColor="white">
                    {sumBase !== sumNew && (
                      <Text fontSize="md" color="gray.500" textDecoration="line-through">{fmtKc(sumBase * 12)}</Text>
                    )}
                    <Text fontWeight="bold" fontSize="lg">{fmtKc(sumNew * 12)}</Text>
                    <DiffChip delta={diffOf(sumBase * 12, sumNew * 12)} />
                  </VStack>
                </Grid>

                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1.5}>Poznámka</Text>
                  <Textarea
                    {...register('note')}
                    placeholder="Důvod mimořádné slevy…"
                    rows={2}
                  />
                </Box>

                <Alert status="warning" fontSize="sm">
                  <AlertIcon />
                  Pozor, bude ovlivněna hned první následující splátka klienta, zkontroluj datum účinnosti. Sleva musí být uplatněna min. 2 dny před účinností.
                </Alert>

                {submitError && (
                  <Alert status="error" fontSize="sm">
                    <AlertIcon />
                    {submitError}
                  </Alert>
                )}
              </VStack>
            </ModalBody>

            <ModalFooter gap={3}>
              <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>Zrušit</Button>
              <Button type="submit" isLoading={isSubmitting} isDisabled={!canSubmit}>Vytvořit verzi</Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}
